// @ts-nocheck
import { useState, useCallback } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Printer,
  AlertCircle,
  Trash2,
  Loader2,
  Pencil,
  Package,
  ExternalLink,
  Undo2,
  Scan,
  Barcode,
  Box,
  CheckCircle2,
  Warehouse,
  ShieldCheck,
  FileText,
  Clock,
  Sparkles,
  RefreshCw,
  DollarSign,
  Lock,
  Receipt,
  Truck,
} from "lucide-react";
import CartonScanDialog from "./components/CartonScanDialog";
import { QuickReceiptDialog } from "@/pages/finance/receipts/components/QuickReceiptDialog";
import EntityLink from "@/components/shared/EntityLink";
import ImageLightbox from "@/components/shared/ImageLightbox";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { numberToVietnameseWords } from "@/lib/numberToWords";

import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatDate, formatMoney } from "@/lib/utils";
import PrintDialog from "@/components/shared/PrintDialog";
import { getStatusConfig } from "@/config/status-registry";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import UserDisplay from "@/components/shared/UserDisplay";

import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { DetailField as F } from "@/components/shared/DetailField";
const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";


export default function DeliveryDetailPage({
  deliveryId: propDeliveryId,
  isZIndex = false,
}: {
  deliveryId?: string;
  isZIndex?: boolean;
} = {}) {
  const params = useParams({ strict: false }) as any;
  const deliveryId = propDeliveryId || params?.deliveryId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [showScanDialog, setShowScanDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const unassignMutation = useMutation({
    mutationFn: async (labelId: string) => {
      return await trpcClient.packingLabels.unassignFromDelivery.mutate({ labelId });
    },
    onSuccess: () => {
      toast.success("Đã gỡ thùng carton khỏi phiếu giao hàng");
      queryClient.invalidateQueries({ queryKey: ["delivery", deliveryId] });
      queryClient.invalidateQueries({ queryKey: ["delivery-cartons", deliveryId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Lỗi khi gỡ thùng carton");
    },
  });

  const [printMode, setPrintMode] = useState<"WITH_PRICE" | "WITHOUT_PRICE">(
    "WITH_PRICE",
  );
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showCoaPrintDialog, setShowCoaPrintDialog] = useState(false);

  const { workflow } = useWorkflowSteps("delivery");

  // Delivery findOne includes: customer, deliveryItems
  const {
    data: delivery,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["delivery", deliveryId],
    queryFn: async () =>
      trpcClient.deliveries.getById.query({ id: deliveryId }),
  });

  const d = delivery as any;

  // Đã thu đủ tiền phiếu giao thì không mời lập thêm phiếu thu (FB-F2TMNF)
  const { data: collection } = useQuery({
    queryKey: ["receipt-collection-status", "DELIVERY", d?.id],
    queryFn: () => trpcClient.receipts.collectionStatus.query({ type: "DELIVERY", id: d.id }),
    enabled: Boolean(d?.id),
  });
  const deliveryFullyPaid =
    !!collection && Number(collection.due || 0) > 0 && Number(collection.remaining || 0) <= 0;

  const handlePrint = useCallback((mode: "WITH_PRICE" | "WITHOUT_PRICE") => {
    setPrintMode(mode);
    setShowPrintDialog(true);
  }, []);

  const handleUpdateStatus = async (newStatus: string) => {
    try {
      await trpcClient.deliveries.updateStatus.mutate({
        id: deliveryId,
        status: newStatus as any,
      });
      await queryClient.invalidateQueries({
        queryKey: ["delivery", deliveryId],
      });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cập nhật trạng thái");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.deliveries.delete.mutate({ id: deliveryId });
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });

      navigate({ to: "/delivery" });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-600" />
      </div>
    );
  }

  if (error || !d) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold">Không tìm thấy phiếu giao hàng</h3>
        <Button variant="link" onClick={() => navigate({ to: "/delivery" })}>
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  const statusCfg = getStatusConfig("DeliveryStatus", d.status);
  const items: any[] = d.deliveryItems || [];
  const goodsItems = items.filter(
    (it: any) => !(it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat"))))
  );
  const vatItem = items.find(
    (it: any) => it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
  );
  const otherServiceItems = items.filter(
    (it: any) => it.isServiceItem && !(it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
  );
  const otherOrders = Array.from(
    new Set(
      items
        .map((it: any) => it.soNumber)
        .filter((so: any) => Boolean(so && so !== d.order?.orderId && so !== d.orderId))
    )
  );

  const goodsAmount = goodsItems.reduce((s: number, it: any) => s + Number(it.amount || 0), 0);
  const vatAmount = vatItem ? Number(vatItem.amount || 0) : 0;
  const deliveryFee = Number(d.deliveryFee || 0);
  const totalAmount =
    d.totalAmount !== undefined && d.totalAmount !== null && Number(d.totalAmount) > 0
      ? Number(d.totalAmount)
      : goodsAmount + vatAmount + deliveryFee;

  const cartons: any[] = d.packingLabels || [];
  const totalCartons = cartons.length;
  const cartonsDispatched = cartons.filter((c: any) => c.isDispatched).length;
  const totalCartonQty = cartons.reduce((sum: number, c: any) => sum + (c.quantity || 0), 0);
  const dispatchPercent = totalCartons > 0 ? Math.round((cartonsDispatched / totalCartons) * 100) : 0;

  const inventoryDoc = d.inventoryDocument;
  const qcFinals: any[] = d.qcFinals || [];

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Giao hàng", to: "/delivery" }]}
      title={d.deliveryId || deliveryId}
      entityId={d.id}
      entityType="delivery"
      isZIndex={isZIndex}
      timestamps={{
        created: d.createdAt,
        updated: d.updatedAt,
        createdBy: d.salesUser?.fullName || d.order?.salesUser?.fullName || d.customer?.salesUsers?.[0]?.user?.fullName,
      }}
      status={
        <Badge variant="outline" className={statusCfg.color}>
          {statusCfg.label || d.status}
        </Badge>
      }
      actions={
        <div className="flex gap-1.5 flex-wrap">
          {/* Thao tác chính của phiếu đứng đầu để luôn hiện ngoài thanh tiêu đề */}
          {(d.status === "DRAFT" || d.status === "SCHEDULED") && (
            <Button
              variant="default"
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap"
              onClick={() => handleUpdateStatus("IN_TRANSIT")}
            >
              <Package className="mr-1.5 h-3.5 w-3.5" /> Duyệt giao
            </Button>
          )}
          <Button
            variant="default"
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap shadow-xs"
            onClick={() => setShowScanDialog(true)}
          >
            <Scan className="mr-1.5 h-3.5 w-3.5" /> Quét thùng
          </Button>
          {(d.status === "DELIVERED" || d.status === "COMPLETED" || d.status === "IN_TRANSIT") &&
            (deliveryFullyPaid ? (
              <Button
                variant="outline"
                size="sm"
                disabled
                data-testid="delivery-fully-paid"
                className="border-emerald-200 bg-emerald-50 text-emerald-700 whitespace-nowrap font-semibold disabled:opacity-100"
              >
                <DollarSign className="mr-1.5 h-3.5 w-3.5" /> Đã thu đủ
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap shadow-xs font-semibold"
                onClick={() => setShowReceiptDialog(true)}
              >
                <DollarSign className="mr-1.5 h-3.5 w-3.5" /> Lập Phiếu Thu
              </Button>
            ))}
          {(d.status === "DRAFT" || d.status === "SCHEDULED") && (
            <>
              <Button
                variant="outline"
                size="sm"
                data-testid="btn-view-update-location"
                className="text-indigo-700 border-indigo-200 hover:bg-indigo-50 whitespace-nowrap"
                onClick={() =>
                  navigate({
                    to: "/delivery/$deliveryId/edit",
                    params: { deliveryId: d.deliveryId },
                    search: { focus: "location" } as any,
                  })
                }
              >
                <Truck className="mr-1.5 h-3.5 w-3.5" /> Vị trí xe
              </Button>
            </>
          )}
          {(d.status === "IN_TRANSIT" || d.status === "DELIVERED") && (
            <Button
              variant="outline"
              size="sm"
              className="text-amber-600 border-amber-200 hover:bg-amber-50 whitespace-nowrap"
              onClick={() => navigate({ to: `/delivery/${deliveryId}/return` })}
            >
              <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Trả hàng
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-emerald-700 border-emerald-300 bg-emerald-50/60 hover:bg-emerald-100 whitespace-nowrap font-semibold"
            onClick={() => setShowCoaPrintDialog(true)}
            data-testid="btn-print-delivery-coa"
          >
            <ShieldCheck className="mr-1.5 h-3.5 w-3.5 text-emerald-600" /> In CoA
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 whitespace-nowrap"
            onClick={() => handlePrint("WITH_PRICE")}
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" /> In có giá
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-blue-600 border-blue-200 hover:bg-blue-50 whitespace-nowrap"
            onClick={() => handlePrint("WITHOUT_PRICE")}
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" /> In không giá
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate({
                to: "/orders/$orderId",
                params: { orderId: d.orderId || "" },
              })
            }
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5 text-slate-500" /> Đơn
            hàng
          </Button>
          {d.status !== "COMPLETED" && d.status !== "DELIVERED" ? (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate({ to: `/delivery/${d.deliveryId}/edit` })}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Sửa
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xoá
              </Button>
            </>
          ) : (
            <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-900 text-xs px-2.5 py-1 flex items-center gap-1 font-semibold">
              <Lock className="w-3.5 h-3.5 text-amber-600" /> Đã Khóa Sổ Bất Biến
            </Badge>
          )}
        </div>
      }
      onBack={() => navigate({ to: "/delivery" })}
      workflow={
        <WorkflowStepper
          workflow={workflow}
          currentStatus={d.status}
          onStatusChange={handleUpdateStatus}
        />
      }
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xác nhận xoá?"
        description={`Bạn sắp xoá phiếu giao hàng ${d.deliveryId}. Dữ liệu sẽ không thể phục hồi.`}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      <div className="space-y-4 w-full mx-auto">
        {/* Immutable Document Guard Banner */}
        {(d.status === "COMPLETED" || d.status === "DELIVERED") && (
          <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg flex items-center gap-3 text-amber-950 text-xs shadow-xs">
            <Lock className="w-5 h-5 text-amber-600 shrink-0" />
            <div>
              <p className="font-bold text-amber-900 text-sm">
                Chứng Từ Đã Khóa Bất Biến 
              </p>
              <p className="text-amber-800 mt-0.5">
                Phiếu giao hàng này đã chuyển trạng thái <strong>{d.status}</strong>. Mọi thay đổi dữ liệu, số lượng hoặc xóa chứng từ đều bị khóa cứng để đảm bảo tính toàn vẹn của Thẻ kho xuất và Sổ cái công nợ khách hàng.
              </p>
            </div>
          </div>
        )}

        {/* ═══ 1. THÔNG TIN CHUNG (Dense Layout) ═══ */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
            <div className="col-span-1 md:col-span-2">
              <F 
                label="Khách hàng" 
                value={
                  <EntityLink 
                    entityType="customer" 
                    id={d.customerId || d.customer?.id} 
                    code={d.customer?.shortName || d.customer?.code} 
                    label={d.customer?.name} 
                  />
                } 
                hl 
              />
            </div>
            <div className="col-span-1">
              <F
                label="Mã KH"
                value={d.customer?.shortName || d.customer?.code}
              />
            </div>
            <div className="col-span-1">
              <F
                label="Đơn hàng"
                value={
                  <div className="flex flex-col gap-0.5">
                    <EntityLink
                      entityType="order"
                      id={d.orderId || d.order?.id}
                      code={d.order?.orderId || (d.orderId?.length > 20 ? "[Xem đơn hàng]" : d.orderId)}
                      label={d.order?.projectName}
                    />
                    {otherOrders.length > 0 && (
                      <span className="text-[11px] text-blue-600 font-medium">
                        + {otherOrders.length} đơn gộp ({otherOrders.join(", ")})
                      </span>
                    )}
                  </div>
                }
                hl
              />
            </div>

            <div className="col-span-1">
              <F label="Ngày giao" value={fmtD(d.deliveryDate)} />
            </div>
            <div className="col-span-1">
              <F
                label="Hình thức giao"
                value={d.deliveryType || "Giao tới khách"}
              />
            </div>
            <div className="col-span-1">
              <F label="Biển số xe" value={d.vehicleNumber} mono />
            </div>
            <div className="col-span-1">
              <F label="Tài xế" value={d.driverName} />
            </div>

            <div className="col-span-1">
              <F
                label="NV Sale"
                value={
                  <UserDisplay
                    name={d.salesUser?.fullName || d.order?.salesUser?.fullName || d.customer?.salesUsers?.[0]?.user?.fullName}
                    avatar={d.salesUser?.avatar || d.order?.salesUser?.avatar}
                  />
                }
              />
            </div>
            <div className="col-span-1 md:col-span-3">
              <F label="Trạm/Chành xe" value={d.transshipmentAddr} />
            </div>

            <div className="col-span-1 md:col-span-2">
              <F
                label="Người nhận"
                value={
                  d.receiverName
                    ? `${d.receiverName} • ${d.receiverPhone || ""}`
                    : "—"
                }
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <F label="ĐC nhận hàng" value={d.address} />
            </div>

            {d.notes && (
              <div className="col-span-1 md:col-span-4 pt-1.5 mt-1.5 border-t border-slate-100">
                <F label="Ghi chú" value={d.notes} />
              </div>
            )}
          </div>
        </div>

        {/* ═══ 2. TABS: Sản phẩm ═══ */}
        <div className="border border-slate-200 rounded-lg bg-white shadow-sm overflow-hidden mt-2">
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger
                value="details"
              >
                <Package className="h-3.5 w-3.5 mr-1.5" />
                Sản phẩm{" "}
                {items.length > 0 && (
                  <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full font-mono">
                    {items.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="cartons"
              >
                <Box className="h-3.5 w-3.5 mr-1.5" />
                Kiện Hàng & Thùng Carton{" "}
                <span
                  className={`ml-1 text-xs px-1.5 py-0.5 rounded-full font-mono ${
                    cartonsDispatched === totalCartons && totalCartons > 0
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {cartonsDispatched}/{totalCartons}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="warehouse"
              >
                <Warehouse className="h-3.5 w-3.5 mr-1.5" />
                Xuất Kho Thành Phẩm{" "}
                <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full font-mono">
                  {inventoryDoc ? "1" : "0"}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="quality"
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                KCS FQC & Nguồn Gốc Lô{" "}
                {qcFinals.length > 0 && (
                  <span className="ml-1 text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-mono">
                    {qcFinals.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="details"
              className="p-0 border-t border-slate-100"
            >
              {/* BẢNG 1: SẢN PHẨM HÀNG HÓA THỰC TẾ (NET PRICE) */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-700">
                      <TableHead className="w-10 text-center">STT</TableHead>
                      <TableHead className="min-w-[180px] whitespace-normal">Sản phẩm</TableHead>
                      <TableHead className="w-14 text-center">ĐVT</TableHead>
                      <TableHead className="w-20 text-right">SL ĐH</TableHead>
                      <TableHead className="w-24 text-right text-blue-700 font-bold whitespace-normal leading-tight">SL Thực giao</TableHead>
                      <TableHead className="w-20 text-right text-amber-600 whitespace-normal leading-tight">SL Bù hao</TableHead>
                      <TableHead className="w-24 text-right text-emerald-800 font-bold bg-emerald-50/40 whitespace-normal leading-tight">SL Thực tính</TableHead>
                      <TableHead className="w-28 text-right whitespace-normal leading-tight">Đơn giá Net</TableHead>
                      <TableHead className="w-32 text-right whitespace-normal leading-tight">Thành tiền Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {goodsItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center text-slate-400 italic">
                          Không có sản phẩm hàng hóa nào trong phiếu giao hàng này
                        </TableCell>
                      </TableRow>
                    ) : (
                      goodsItems.map((it, idx) => (
                        <TableRow key={idx} className="hover:bg-slate-50 group border-b">
                          <TableCell className="text-center text-slate-500 text-xs align-middle">
                            {idx + 1}
                          </TableCell>
                          <TableCell className="align-middle whitespace-normal">
                            <div className="flex items-center gap-2.5">
                              <ProductionThumbnailCell
                                imageUrl={it.product?.image || it.productImage}
                                layoutUrl={it.printLayoutUrl || it.printLayout?.fileUrl}
                                title={it.productName || it.product?.name}
                                size={32}
                                className="shrink-0"
                              />
                              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                {it.product?.isDeleted ? (
                                  // Sản phẩm đã bị xoá: không dẫn tới trang sản phẩm (sẽ báo không tìm thấy)
                                  <span className="text-sm font-bold text-slate-900">
                                    {it.productName || it.product?.productName}
                                    <span className="ml-1.5 text-[11px] font-medium text-rose-600">(sản phẩm đã xoá)</span>
                                  </span>
                                ) : (
                                  <EntityLink
                                    entityType="product"
                                    id={it.productId}
                                    code={it.product?.productCode}
                                    label={it.productName || it.product?.name}
                                    className="text-sm font-bold text-slate-900"
                                    search={{ returnUrl: `/delivery/${deliveryId}` }}
                                  />
                                )}
                                {it.notes && (
                                  <div className="text-[11px] text-slate-500 italic">
                                    {it.notes}
                                  </div>
                                )}
                                {(it.poNumber || it.soNumber) && (
                                  <div className="flex gap-2 text-[10px] text-slate-400 font-mono">
                                    {it.poNumber && <span>PO: {it.poNumber}</span>}
                                    {it.soNumber && <span>ĐH: {it.soNumber}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-xs text-slate-600 align-middle">
                            {it.unit || "Hộp"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700 font-medium align-middle">
                            {formatNumber(it.quantity)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums align-middle font-bold text-blue-700">
                            {formatNumber(it.deliveredQty)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-amber-600 align-middle">
                            {it.lossQty != null && it.lossQty !== 0
                              ? formatNumber(it.lossQty)
                              : it.lossQty === 0
                                ? "0"
                                : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-bold text-emerald-800 align-middle bg-emerald-50/30">
                            {formatNumber(it.countedQty)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-600 align-middle font-medium">
                            {it.unitPrice != null
                              ? formatNumber(it.unitPrice)
                              : "—"} ₫
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-bold text-slate-900 align-middle">
                            {it.amount != null ? formatNumber(it.amount) : "—"} ₫
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {/* Subtotal Goods Row */}
                    <TableRow className="bg-slate-50/80 font-bold border-t-2">
                      <TableCell colSpan={3} className="text-right text-xs text-slate-700 px-3 uppercase">
                        Cộng Tiền Hàng Net
                      </TableCell>
                      <TableCell className="text-right tabular-nums px-2">
                        {formatNumber(goodsItems.reduce((s, it) => s + Number(it.quantity || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-blue-700 px-2 font-bold">
                        {formatNumber(goodsItems.reduce((s, it) => s + Number(it.deliveredQty || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-amber-600 px-2">
                        {formatNumber(goodsItems.reduce((s, it) => s + Number(it.lossQty || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-bold text-emerald-800 px-2">
                        {formatNumber(goodsItems.reduce((s, it) => s + Number(it.countedQty || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right px-3 text-slate-300">—</TableCell>
                      <TableCell className="text-right tabular-nums px-2 font-black text-slate-900 border-l border-slate-200">
                        {formatMoney(goodsAmount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* BẢNG 2: PHỤ PHÍ DỊCH VỤ & THUẾ GTGT (NẾU CÓ) */}
              {(otherServiceItems.length > 0 || vatItem) && (
                <div className="border-t border-slate-200 p-4 bg-slate-50/30 space-y-2">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-purple-600" />
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-tight">
                      Khoản Mục Dịch Vụ Phụ Trợ & Thuế GTGT
                    </h4>
                  </div>
                  <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/90 text-[10px] uppercase text-slate-600">
                          <TableHead className="w-10 text-center">STT</TableHead>
                          <TableHead className="min-w-[200px]">Khoản mục / Dịch vụ</TableHead>
                          <TableHead className="w-20 text-center">ĐVT</TableHead>
                          <TableHead className="w-20 text-right">SL</TableHead>
                          <TableHead className="w-28 text-right">Đơn giá</TableHead>
                          <TableHead className="w-32 text-right">Thành tiền</TableHead>
                          <TableHead className="text-left">Ghi chú</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Other Service Items */}
                        {otherServiceItems.map((sit: any, sIdx: number) => (
                          <TableRow key={`srv-${sIdx}`} className="text-xs border-b">
                            <TableCell className="text-center text-slate-400">{sIdx + 1}</TableCell>
                            <TableCell className="font-semibold text-purple-900 whitespace-normal">
                              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 border-purple-200 text-[10px] px-1.5 py-0 mr-1.5 font-normal">
                                DỊCH VỤ
                              </Badge>
                              {sit.productName}
                            </TableCell>
                            <TableCell className="text-center">{sit.unit || "Gói"}</TableCell>
                            <TableCell className="text-right font-medium">{formatNumber(sit.deliveredQty || 1)}</TableCell>
                            <TableCell className="text-right">{formatMoney(sit.unitPrice || 0)}</TableCell>
                            <TableCell className="text-right font-bold text-purple-900">{formatMoney(sit.amount || 0)}</TableCell>
                            <TableCell className="text-slate-500 italic text-[11px] whitespace-normal">{sit.notes || "—"}</TableCell>
                          </TableRow>
                        ))}
                        {/* VAT Line */}
                        {vatItem && (
                          <TableRow className="text-xs bg-indigo-50/40 border-b border-indigo-100">
                            <TableCell className="text-center text-indigo-400 font-bold">*</TableCell>
                            <TableCell className="font-bold text-indigo-900 flex items-center gap-1.5 whitespace-normal">
                              <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 border-indigo-200 text-[10px] px-1.5 py-0 font-bold">
                                THUẾ GTGT
                              </Badge>
                              {vatItem.productName}
                            </TableCell>
                            <TableCell className="text-center">{vatItem.unit || "Gói"}</TableCell>
                            <TableCell className="text-right font-medium">1</TableCell>
                            <TableCell className="text-right font-mono text-indigo-700">{formatMoney(vatItem.unitPrice || vatAmount)}</TableCell>
                            <TableCell className="text-right font-black text-indigo-800 font-mono">{formatMoney(vatAmount)}</TableCell>
                            <TableCell className="text-indigo-600 italic text-[11px] whitespace-normal">
                              {vatItem.notes || `Thuế GTGT tính trên tiền hàng (${formatMoney(goodsAmount)})`}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* KHỐI TỔNG KẾT TÀI CHÍNH ĐA TẦNG (FINANCIAL RECONCILIATION SUMMARY) */}
              <div className="p-4 bg-slate-50/90 border-t border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="space-y-1 text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-700">Mã chứng từ:</span>
                    <span className="font-mono text-slate-900 font-bold">{d.deliveryId || deliveryId}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-700">Số lượng mặt hàng:</span>
                    <span>{goodsItems.length} SP {vatItem ? "+ 1 Dịch vụ VAT" : ""}</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1.5 min-w-[320px]">
                  {/* 1. Tiền hàng Net */}
                  <div className="flex items-center justify-between w-full text-xs">
                    <span className="text-slate-600 font-medium">1. Tổng tiền hàng Net (chưa VAT):</span>
                    <span className="font-bold text-slate-900 tabular-nums">
                      {formatMoney(goodsAmount)}
                    </span>
                  </div>

                  {/* 2. Dịch vụ VAT */}
                  {vatItem && (
                    <div className="flex items-center justify-between w-full text-xs">
                      <span className="text-indigo-700 font-semibold flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-indigo-500" /> 2. Thuế GTGT:
                      </span>
                      <span className="font-bold text-indigo-700 tabular-nums">
                        +{formatMoney(vatAmount)}
                      </span>
                    </div>
                  )}

                  {/* 3. Phí vận chuyển */}
                  {deliveryFee > 0 && (
                    <div className="flex items-center justify-between w-full text-xs">
                      <span className="text-slate-600 font-medium">3. Phí vận chuyển / bốc xếp:</span>
                      <span className="font-semibold text-slate-800 tabular-nums">
                        +{formatMoney(deliveryFee)}
                      </span>
                    </div>
                  )}

                  {/* 4. Tổng cộng thanh toán */}
                  <div className="flex items-center justify-between w-full pt-2 border-t-2 border-slate-300 mt-0.5">
                    <span className="text-xs font-black text-slate-900 uppercase tracking-tight">
                      TỔNG CỘNG THANH TOÁN:
                    </span>
                    <span className="text-xl font-black text-emerald-700 tabular-nums">
                      {formatMoney(totalAmount)}
                    </span>
                  </div>

                  {/* 5. Bằng chữ */}
                  {totalAmount > 0 && (
                    <div className="text-[11px] text-slate-600 italic text-right max-w-[360px] leading-snug mt-0.5">
                      (Bằng chữ: <strong>{numberToVietnameseWords(totalAmount)}</strong>)
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ═══ TAB 2: KIỆN HÀNG & THÙNG CARTON ═══ */}
            <TabsContent value="cartons" className="p-4 space-y-4">
              {/* Header bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                    <Box className="h-4 w-4 text-indigo-600" />
                    Quản Lý Thùng Carton Đóng Gói Lên Chuyến Xe
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Đối soát tem thùng carton (`LBL-...`), tiến độ xếp dỡ và kiểm đếm trước khi xe rời kho
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs"
                    onClick={() => setShowScanDialog(true)}
                  >
                    <Scan className="h-3.5 w-3.5 mr-1.5" /> Trạm Quét Mã Vạch (Súng / Camera)
                  </Button>
                </div>
              </div>

              {/* Realtime KPI stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 font-medium">Tiến độ xếp xe</span>
                    <span className="text-xs font-mono font-bold text-indigo-600">
                      {dispatchPercent}%
                    </span>
                  </div>
                  <div className="text-lg font-bold font-mono text-slate-900 mt-1">
                    {cartonsDispatched}{" "}
                    <span className="text-xs text-slate-500 font-normal">/ {totalCartons} thùng</span>
                  </div>
                  <Progress value={dispatchPercent} className="h-1.5 mt-2 bg-slate-100" />
                </div>

                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                  <span className="text-xs text-slate-500 font-medium">Tổng SL sản phẩm trong kiện</span>
                  <div className="text-lg font-bold font-mono text-indigo-600 mt-1">
                    {formatNumber(totalCartonQty)}{" "}
                    <span className="text-xs text-slate-500 font-normal">hộp</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Đã quy chuẩn theo tem đóng gói</p>
                </div>

                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                  <span className="text-xs text-slate-500 font-medium">Trạng thái kiểm đếm</span>
                  <div className="mt-1">
                    {totalCartons === 0 ? (
                      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                        Chưa gán thùng
                      </Badge>
                    ) : cartonsDispatched === totalCartons ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs font-semibold">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> 100% ĐÃ LÊN XE
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs">
                        Còn {totalCartons - cartonsDispatched} thùng chờ quét
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {cartonsDispatched === totalCartons && totalCartons > 0
                      ? "Toàn bộ kiện hàng đã được quét xếp xe an toàn"
                      : "Dùng súng bắn barcode để xác nhận các thùng còn lại"}
                  </p>
                </div>
              </div>

              {/* Cartons Table */}
              {cartons.length === 0 ? (
                <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-400 text-xs">
                  <Box className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  Chưa có thùng carton nào được gán vào chuyến xe này.
                  <br />
                  Bấm <strong className="text-indigo-600">"Trạm Quét Mã Vạch"</strong> để quét mã tem `LBL-...` và tự động gán vào phiếu giao hàng.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="w-12 text-center">STT</TableHead>
                        <TableHead className="w-36">Mã Tem Barcode</TableHead>
                        <TableHead className="w-28">Thứ Tự Kiện</TableHead>
                        <TableHead className="w-28 text-right">SL / Thùng</TableHead>
                        <TableHead className="min-w-[160px]">Lệnh Sản Xuất</TableHead>
                        <TableHead className="w-32">Trạng Thái</TableHead>
                        <TableHead className="w-40">Thời Gian Quét</TableHead>
                        <TableHead className="w-20 text-center">Thao Tác</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cartons.map((carton, idx) => (
                        <TableRow key={carton.id || idx} className="hover:bg-slate-50">
                          <TableCell className="text-center text-xs text-slate-500 font-mono">
                            {idx + 1}
                          </TableCell>
                          <TableCell className="font-mono font-bold text-xs text-slate-900">
                            {carton.code}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">
                            Thùng {carton.cartonIndex} / {carton.totalCartons || "—"}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono font-semibold text-indigo-600">
                            {formatNumber(carton.quantity || 0)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {carton.productionOrderId ? (
                              <EntityLink
                                type="productionOrder"
                                id={carton.productionOrderId}
                                label={carton.productionOrder?.orderId || carton.productionOrderId}
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            {carton.isDispatched ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-semibold">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> ĐÃ LÊN XE
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
                                CHỜ QUÉT
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-[11px] text-slate-500 font-mono">
                            {carton.dispatchedAt
                              ? `${new Date(carton.dispatchedAt).toLocaleTimeString("vi-VN")} ${new Date(carton.dispatchedAt).toLocaleDateString("vi-VN")}`
                              : "—"}
                          </TableCell>
                          <TableCell className="text-center">
                            {d.status !== "DELIVERED" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => unassignMutation.mutate(carton.id)}
                                disabled={unassignMutation.isPending}
                                className="h-6 px-2 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                title="Gỡ thùng này khỏi chuyến xe"
                              >
                                Gỡ
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* ═══ TAB 3: XUẤT KHO THÀNH PHẨM ═══ */}
            <TabsContent value="warehouse" className="p-4 space-y-4">
              {inventoryDoc ? (
                <div className="space-y-4">
                  {/* Warehouse doc info card */}
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Warehouse className="h-4 w-4 text-indigo-600" />
                          <h3 className="text-sm font-bold text-slate-900 font-mono">
                            Phiếu Xuất Kho: {inventoryDoc.docNo}
                          </h3>
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-semibold">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> ĐÃ HOÀN TẤT
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Chứng từ xuất kho tự động trừ tồn kho Thành Phẩm (KHO-TP) theo đơn giao hàng
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-slate-400">Ngày xuất kho: </span>
                        <span className="text-xs font-semibold text-slate-800">{fmtD(inventoryDoc.docDate)}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                      <div>
                        <span className="text-slate-400">Kho xuất:</span>
                        <p className="font-semibold text-slate-800 mt-0.5">
                          {inventoryDoc.sourceWarehouse?.name || "KHO-TP (Kho Thành Phẩm)"}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-400">Loại nghiệp vụ:</span>
                        <p className="font-semibold text-slate-800 mt-0.5">Xuất giao hàng khách (ISSUE)</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Phương tiện / Tài xế:</span>
                        <p className="font-semibold text-slate-800 mt-0.5">
                          {d.vehicleNumber ? `${d.vehicleNumber} (${d.driverName || "—"})` : "—"}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-400">Số dòng hàng xuất:</span>
                        <p className="font-semibold text-indigo-600 font-mono mt-0.5">
                          {inventoryDoc.lines?.length || 0} dòng
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lines Table */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader className="bg-slate-50">
                        <TableRow>
                          <TableHead className="w-12 text-center">STT</TableHead>
                          <TableHead className="w-32">Mã Vật Tư / SP</TableHead>
                          <TableHead className="min-w-[200px]">Tên Hàng Hóa</TableHead>
                          <TableHead className="w-20 text-center">ĐVT</TableHead>
                          <TableHead className="w-28 text-right">SL Xuất</TableHead>
                          <TableHead className="w-28 text-right">Đơn Giá</TableHead>
                          <TableHead className="w-32 text-right">Thành Tiền</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(inventoryDoc.lines || []).map((line: any, idx: number) => (
                          <TableRow key={line.id || idx} className="hover:bg-slate-50">
                            <TableCell className="text-center text-xs text-slate-500 font-mono">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="font-mono text-xs font-semibold text-indigo-600">
                              {line.itemCode || "—"}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-slate-900">
                              {line.itemName || "—"}
                            </TableCell>
                            <TableCell className="text-center text-xs text-slate-600">
                              {line.unit || "Hộp"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-xs text-blue-700">
                              {formatNumber(line.quantity || 0)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-slate-600">
                              {line.unitPrice ? formatNumber(Number(line.unitPrice)) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-xs text-slate-900">
                              {line.amount ? formatNumber(Number(line.amount)) : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                  <Warehouse className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <h4 className="text-sm font-bold text-slate-700">Chưa phát sinh chứng từ xuất kho KHO-TP</h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 leading-relaxed">
                    Chứng từ xuất kho tự động sẽ được hệ thống sinh từ Kho Thành Phẩm (KHO-TP) và trừ tồn kho khi phiếu giao hàng chuyển sang trạng thái <strong>Đang Vận Chuyển (IN_TRANSIT)</strong> hoặc <strong>Đã Giao (DELIVERED)</strong>.
                  </p>
                  {(d.status === "DRAFT" || d.status === "SCHEDULED") && (
                    <Button
                      size="sm"
                      className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                      onClick={() => handleUpdateStatus("IN_TRANSIT")}
                    >
                      <Package className="h-3.5 w-3.5 mr-1.5" /> Duyệt giao
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>

            {/* ═══ TAB 4: KCS FQC & NGUỒN GỐC LÔ ═══ */}
            <TabsContent value="quality" className="p-4 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    Biên Bản Nghiệm Thu KCS FQC & Chứng Nhận Xuất Xưởng (CoA)
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Hồ sơ kiểm định FQC và phát hành Giấy chứng nhận chất lượng (Certificate of Analysis) theo chuyến giao
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold gap-1.5 shrink-0"
                  onClick={() => setShowCoaPrintDialog(true)}
                >
                  <Printer className="h-3.5 w-3.5" /> Phát Hành & In CoA (A4)
                </Button>
              </div>

              {qcFinals.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                  <ShieldCheck className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <h4 className="text-sm font-bold text-slate-700">Chưa có hồ sơ FQC liên kết</h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto mt-1">
                    Chưa tìm thấy biên bản nghiệm thu FQC tương ứng với các mã lệnh sản xuất trong phiếu giao hàng này.
                  </p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="w-12 text-center">STT</TableHead>
                        <TableHead className="w-36">Mã Biên Bản KCS</TableHead>
                        <TableHead className="min-w-[160px]">Lệnh Sản Xuất</TableHead>
                        <TableHead className="min-w-[180px]">Sản Phẩm</TableHead>
                        <TableHead className="w-28 text-center">Kết Quả</TableHead>
                        <TableHead className="w-28 text-right">SL Kiểm / Đạt</TableHead>
                        <TableHead className="w-32">Thanh Tra Viên</TableHead>
                        <TableHead className="w-28">Ngày Kiểm</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {qcFinals.map((qc: any, idx: number) => {
                        const isPassed = qc.status === "PASSED";
                        return (
                          <TableRow key={qc.id || idx} className="hover:bg-slate-50">
                            <TableCell className="text-center text-xs text-slate-500 font-mono">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="font-mono font-bold text-xs">
                              <EntityLink
                                type="qcFinal"
                                id={qc.id}
                                label={qc.finalQcId || qc.id}
                              />
                            </TableCell>
                            <TableCell className="text-xs">
                              {qc.productionOrder ? (
                                <EntityLink
                                  type="productionOrder"
                                  id={qc.productionOrder.id}
                                  label={qc.productionOrder.orderId}
                                />
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-slate-900">
                              {qc.productionOrder?.productName || "—"}
                            </TableCell>
                            <TableCell className="text-center">
                              {isPassed ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-semibold">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> ĐẠT CHUẨN
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
                                  CẢNH BÁO
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs font-mono">
                              <span className="text-emerald-600 font-bold">
                                {formatNumber(qc.qtyPassed || 0)}
                              </span>{" "}
                              / {formatNumber(qc.qtyChecked || 0)}
                            </TableCell>
                            <TableCell className="text-xs text-slate-700">
                              {qc.inspector?.fullName || qc.inspector?.email || "KCS"}
                            </TableCell>
                            <TableCell className="text-xs text-slate-500 font-mono">
                              {fmtD(qc.checkTime)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <CartonScanDialog
          open={showScanDialog}
          onOpenChange={setShowScanDialog}
          deliveryId={d.id}
          deliveryCode={d.deliveryId}
          customerName={d.customer?.shortName || d.customer?.name}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["delivery", deliveryId] });
          }}
        />

        <QuickReceiptDialog
          open={showReceiptDialog}
          onOpenChange={setShowReceiptDialog}
          source={{
            type: "DELIVERY",
            id: d.id,
            code: d.deliveryId,
            customerName: d.customer?.shortName || d.customer?.name,
            defaultAmount: totalAmount,
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["delivery", deliveryId] });
          }}
        />

        <PrintDialog
          open={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
          type="delivery"
          data={{ ...d, hidePrice: printMode === "WITHOUT_PRICE" }}
        />

        <PrintDialog
          open={showCoaPrintDialog}
          onClose={() => setShowCoaPrintDialog(false)}
          type="coa"
          data={d}
        />
      </div>
    </StandardDetailLayout>
  );
}
